// Contact Cards Manager Component
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Phone, Mail, MapPin, User, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { ContactCardForm } from './AdminShared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CARD_TYPES = [
  { value: 'person', label: 'Personne' },
  { value: 'address', label: 'Adresse' },
  { value: 'sales_point', label: 'Point de vente' },
  { value: 'secretary', label: 'Secrétariat' }
];

const ContactInfoManager = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { fetchCards(); }, []);

  const fetchCards = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact-cards`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCards(data || []);
      }
    } catch (err) {
      console.error('Error fetching contact cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette carte ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/contact-cards/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchCards();
    } catch (err) {
      console.error('Error deleting card:', err);
    }
  };

  const handleSave = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/contact-cards/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setEditingId(null);
      fetchCards();
    } catch (err) {
      console.error('Error saving card:', err);
    }
  };

  const handleAdd = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/contact-cards`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setShowAddForm(false);
      setEditForm({});
      fetchCards();
    } catch (err) {
      console.error('Error adding card:', err);
    }
  };

  const getCardIcon = (cardType) => {
    switch(cardType) {
      case 'person': case 'secretary': return User;
      case 'address': return MapPin;
      case 'sales_point': return Building2;
      default: return User;
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Cartes de Contact</h2>
          <p className="text-sm text-slate-500 mt-1">Gérez les informations de contact affichées sur la page Contact</p>
        </div>
        <Button onClick={() => {
          setShowAddForm(true);
          setEditForm({ card_type: 'person', title_en: '', title_fr: '', title_ar: '', name: '', phones: [], emails: [], address: '', order: cards.length, is_active: true });
        }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {showAddForm && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-800 mb-4">Nouvelle Carte</h3>
          <ContactCardForm form={editForm} setForm={setEditForm} cardTypes={CARD_TYPES} onSave={handleAdd} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      <div className="space-y-4">
        {cards.sort((a, b) => a.order - b.order).map((card) => {
          const Icon = getCardIcon(card.card_type);
          return (
            <div key={card.id} className="border border-slate-200 rounded-lg p-4">
              {editingId === card.id ? (
                <ContactCardForm form={editForm} setForm={setEditForm} cardTypes={CARD_TYPES} onSave={() => handleSave(card.id)} onCancel={() => setEditingId(null)} />
              ) : (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon className="text-slate-400" size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{CARD_TYPES.find(t => t.value === card.card_type)?.label}</span>
                      <span className="text-xs text-slate-400">#{card.order}</span>
                      {!card.is_active && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Inactif</span>}
                    </div>
                    <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">{card.title_fr}</p>
                    {card.name && <p className="font-medium text-slate-800">{card.name}</p>}
                    {card.phones && card.phones.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-1">
                        {card.phones.map((phone, index) => <span key={index} className="text-sm text-slate-500 flex items-center gap-1"><Phone size={12} /> {phone}</span>)}
                      </div>
                    )}
                    {card.emails && card.emails.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-1">
                        {card.emails.map((email, index) => <span key={index} className="text-sm text-slate-500 flex items-center gap-1"><Mail size={12} /> {email}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingId(card.id); setEditForm(card); }}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(card.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {cards.length === 0 && !showAddForm && <div className="text-center py-12 text-slate-500">Aucune carte de contact.</div>}
    </div>
  );
};

export default ContactInfoManager;
